import { PostHogProvider as PHProvider, usePostHog } from '@posthog/react';
import posthog from 'posthog-js';
import { sampleByEvent } from 'posthog-js/lib/src/customizations';
import type { ReactNode } from 'react';
import { Suspense, useEffect } from 'react';
import { getEnvironment, IS_DEVELOPMENT } from '../helpers';

const PUBLIC_POSTHOG_KEY = 'phc_gwHI3AI4eicwpkiihzeeqgHCpLmRHUTOP2nJ3AJqEbr';
const PUBLIC_POSTHOG_HOST = 'https://us.i.posthog.com'; // this can be replaced with a custom domain eg. https://events.pulsepoint.com when we setup a reverse proxy

const IGNORED_RESIZE_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

function PostHogPageView() {
  const posthogContext = usePostHog();

  useEffect(() => {
    if (posthog) {
      posthogContext.capture('$pageview', {
        $current_url: window.location.href,
      });

      posthog.register({
        $host_url: window.location.host,
        $environment: getEnvironment(window.location.host),
      });
    }
  }, [posthogContext]);

  return null;
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  const isLookerDev = getEnvironment(window.location.hostname) === 'dev';

  useEffect(() => {
    if (!PUBLIC_POSTHOG_KEY) {
      return;
    }

    if (IS_DEVELOPMENT || isLookerDev) return;

    posthog.init(PUBLIC_POSTHOG_KEY, {
      api_host: PUBLIC_POSTHOG_HOST,
      ui_host: PUBLIC_POSTHOG_HOST,
      capture_pageview: true,
      capture_pageleave: false,
      debug: process.env.NODE_ENV === 'development',
      before_send: (event) => {
        const sampledEvent = sampleByEvent(['$web_vitals'], 0.5)(event);

        if (sampledEvent?.event === '$exception') {
          const errorMessage = sampledEvent.properties?.$exception_message;
          if (typeof errorMessage === 'string' && IGNORED_RESIZE_ERRORS.some((msg) => errorMessage.includes(msg))) {
            return null;
          }

          sampledEvent.properties.host_url = window.location.host;
          sampledEvent.properties.environment = getEnvironment(window.location.host);
        }

        return sampledEvent;
      },
    });
  }, [isLookerDev]);

  return (
    <PHProvider client={posthog}>
      {!IS_DEVELOPMENT && !isLookerDev && <SuspendedPostHogPageView />}
      {children}
    </PHProvider>
  );
}
